import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthImage } from '@/components/AuthImage';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { fieldsFor } from '@/lib/customFields';
import { Camera, Loader2, Trash2, Image as ImageIcon } from 'lucide-react';

/**
 * Reusable renderer for custom fields.
 * Props:
 *  - defs: full custom_fields definition array
 *  - context: 'inventory' | 'billing'
 *  - value: { [key]: any }
 *  - onChange: (nextValueObject) => void
 *  - errors: { [key]: message }
 *  - idPrefix: string for stable test ids
 */
export function CustomFieldsForm({ defs, context, value = {}, onChange, errors = {}, idPrefix = 'cf' }) {
  const fields = fieldsFor(defs, context);
  const [uploading, setUploading] = useState({});
  if (!fields.length) return null;

  const set = (key, v) => onChange({ ...value, [key]: v });

  const uploadPhotos = async (key, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const cur = value[key];
      const existing = Array.isArray(cur) ? cur : (cur && cur.path ? [cur] : []);
      const added = [];
      for (const file of list) {
        if (!file.type?.startsWith('image/')) { toast.error(`${file.name || 'File'} is not an image`); continue; }
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name || 'Image'} is too large (max 8 MB)`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        added.push({ path: r.data.data.path, content_type: r.data.data.content_type, size: r.data.data.size });
      }
      if (added.length) {
        set(key, [...existing, ...added]);
        toast.success(added.length > 1 ? `${added.length} photos added` : 'Photo added');
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading((u) => ({ ...u, [key]: false })); }
  };

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-form`}>
      {fields.map((f) => {
        const v = value[f.key];
        const err = errors[f.key];
        const tid = `${idPrefix}-${f.key}`;
        const labelEl = (
          <Label className="text-xs">
            {f.name}{f.required && <span className="ml-0.5 text-destructive">*</span>}
          </Label>
        );
        return (
          <div key={f.id || f.key} data-testid={`${idPrefix}-field-${f.key}`}>
            {f.type !== 'boolean' && labelEl}
            {f.type === 'text' && (
              <Input value={v || ''} onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 h-11 rounded-xl" data-testid={tid} />
            )}
            {f.type === 'number' && (
              <Input inputMode="decimal" value={v ?? ''} onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 h-11 rounded-xl" data-testid={tid} />
            )}
            {f.type === 'date' && (
              <Input type="date" value={v || ''} onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 h-11 rounded-xl" data-testid={tid} />
            )}
            {f.type === 'dropdown' && (
              <Select value={v || ''} onValueChange={(val) => set(f.key, val)}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {(f.options || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            {f.type === 'boolean' && (
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                {labelEl}
                <Switch checked={v === true} onCheckedChange={(val) => set(f.key, val)} data-testid={tid} />
              </div>
            )}
            {f.type === 'photo' && (() => {
              const photos = Array.isArray(v) ? v : (v && v.path ? [v] : []);
              const removeAt = (idx) => set(f.key, photos.filter((_, i) => i !== idx));
              return (
                <div className="mt-1">
                  {photos.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {photos.map((p, idx) => (
                        <div key={p.path || idx} className="relative">
                          <AuthImage path={p.path} alt={f.name} className="h-16 w-16 rounded-lg object-cover ring-1 ring-border" />
                          <button type="button" onClick={() => removeAt(idx)} data-testid={`${tid}-remove-${idx}`}
                            className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-xs font-medium hover:bg-secondary/60">
                      {uploading[f.key] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}Take photo
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => { uploadPhotos(f.key, e.target.files); e.target.value = ''; }} data-testid={tid} />
                    </label>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-xs font-medium hover:bg-secondary/60">
                      <ImageIcon className="h-4 w-4" />Gallery / device
                      <input type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => { uploadPhotos(f.key, e.target.files); e.target.value = ''; }} data-testid={`${tid}-gallery`} />
                    </label>
                    {photos.length > 0 && <span className="text-[11px] text-muted-foreground">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>}
                  </div>
                </div>
              );
            })()}
            {err && <p className="mt-1 text-[11px] text-destructive" data-testid={`${tid}-error`}>{err}</p>}
          </div>
        );
      })}
    </div>
  );
}
